<template>
  <v-dialog
    v-model="openSelection"
    class="bg-white dark-bg-custom"
    :max-width="dialogMaxWidth"
    persistent
  >
    <v-card class="bg-white dark-bg-custom" v-if="!stateFetch">
      <div>
        <div class="flex justify-between items-center border-b px-5 py-4">
          <h3 class="text-xl font-medium m-0">
            {{ isExportDialog ? 'Download Attendance Matrix' : 'Export Report' }}
          </h3>
          <v-btn icon @click="cancel">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="flex h-[500px]">
          <!-- Left Panel: Available Columns -->
          <div class="w-1/3 border-r flex flex-col">
            <div class="overflow-y-auto flex-1 p-3">
              <div class="mb-4 border-b flex justify-between items-center">
                <span class="font-medium">Available Columns</span>
              </div>

              <div v-for="(category, catIndex) in availableCategories" :key="catIndex" class="mb-4">
                <div class="font-semibold text-gray-700 mb-2 uppercase text-xs tracking-wider">
                  {{ category.name }}
                </div>
                <div v-for="(col, colIndex) in category.columns" :key="colIndex" class="ml-2">
                  <v-checkbox
                    v-model="tempSelectedColumns"
                    :label="col"
                    :value="col"
                    dense
                    hide-details
                    class="my-1 text-sm custom-checkbox"
                    @change="onSelectionChange"
                  ></v-checkbox>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Panel: Selected Columns -->
          <div class="w-2/3 flex flex-col">
            <div class="overflow-y-auto flex-1 p-3 bg-gray-50/50">
              <div class="mb-4 border-b flex justify-between items-center">
                <span class="font-medium">Selected Columns ({{ tempSelectedColumns.length }})</span>
                <v-btn
                  text
                  small
                  color="primary"
                  @click="clearAll"
                  v-if="tempSelectedColumns.length"
                >
                  Clear All
                </v-btn>
              </div>
              <v-chip-group column>
                <v-chip
                  v-for="col in tempSelectedColumns"
                  :key="col"
                  close
                  small
                  color="blue lighten-5"
                  text-color="blue darken-3"
                  @click:close="removeColumn(col)"
                  class="ma-1 font-medium"
                >
                  {{ col }}
                </v-chip>
              </v-chip-group>

              <div
                v-if="!tempSelectedColumns.length"
                class="flex flex-col items-center justify-center h-full text-gray-400"
              >
                <v-icon color="grey lighten-1" size="48">mdi-table-column-remove</v-icon>
                <span class="mt-2">No columns selected</span>
              </div>
            </div>
          </div>
        </div>

        <div class="px-5 py-4 border-t flex justify-between items-center bg-gray-50">
          <div>
            <p class="text-xs text-gray-500 mb-0">Review valid selections before downloading.</p>
          </div>
          <div class="flex justify-end gap-3">
            <v-btn outlined @click="cancel" class="px-6" small> Cancel </v-btn>
            <v-btn
              :color="$vuetify.theme.currentTheme.primary"
              class="white--text px-6"
              @click="printData"
              small
              :disabled="!tempSelectedColumns.length"
            >
              {{ isExportDialog ? 'Download Matrix' : 'Download Report' }}
            </v-btn>
          </div>
        </div>
      </div>
    </v-card>

    <v-container class="bg-white dark-bg-custom" style="height: 300px" v-else>
      <v-row class="fill-height flex-col" align-content="center" justify="center">
        <v-col class="text-h6 text-center" cols="12"> Preparing Data... </v-col>
        <v-col cols="6">
          <v-progress-linear color="primary" indeterminate rounded height="6"></v-progress-linear>
        </v-col>
        <v-col class="text-subtitle-2 text-center text-gray-500" cols="12">
          Fetching latest data...
        </v-col>
      </v-row>
    </v-container>
  </v-dialog>
</template>

<script>
import ExcelJS from 'exceljs'
// import ExcelJS from 'exceljs'
import { sortDropdownOptions, isModuleFeatureAllowed } from '~/utils/common'

export default {
  data() {
    return {
      extraOptions: [],
      reporteeChain: this.$storage.getUniversal('userReporteeChain'),
      orgLang: this.$store.state.orgLanguage,
      subCases: this.$store.state.subCases,

      openSelection: false,
      formattedData: [],
      isExportDialog: false,
      maxManagers: 0,
      dialogMaxWidth: '1000px',

      // Internal tracking of selected columns
      tempSelectedColumns: []
    }
  },
  props: {
    items: Array, // Employee Data
    currentMonthDates: Array,
    selectedColumns: Array, // "Attendance" columns passed from TeamReview
    startDate: String,
    endDate: String,
    stateFetch: Boolean,
    attendanceSettings: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    tabMapping() {
      const firstSubCase = this.subCases?.[0]?.subCase?.[0]
      return firstSubCase?.tableTabMapping || {}
    },
    orgLanguage() {
      return this.orgLang.data.leftNav
    },
    attendanceSubColumns() {
      return [
        { label: 'Attendance', visible: true },
        { label: 'First Clock-In', visible: true },
        // { label: 'Last Clock-In', visible: true },
        { label: 'Last Clock-Out', visible: true },
        { label: 'Planned Working Hour', visible: true },
        { label: 'Completed Working Hour', visible: true },
        // { label: 'Status', visible: true },
        { label: 'Overtime', visible: true },
        {
          label: 'Clock-In Image',
          visible: this.isAttendanceFeature('CLOCKIN_WITH_SELFIE')
        },
        {
          label: 'Clock-Out Image',
          visible: this.isAttendanceFeature('CLOCKOUT_WITH_SELFIE')
        },
        { label: 'Delay In Shift', visible: true },
        { label: 'Breaks Count', visible: true },
        { label: 'Distance (km)', visible: true }
      ]
    },
    availableCategories() {
      const visibleCols = this.attendanceSubColumns
        .filter((opt) => opt.visible)
        .map((opt) => opt.label)

      return [
        {
          name: 'Attendance Details',
          columns: visibleCols
        }
      ]
    }
  },
  methods: {
    isAttendanceFeature(feature) {
      return isModuleFeatureAllowed('ATTENDANCE_MANAGEMENT', feature)
    },
    removeColumn(col) {
      this.tempSelectedColumns = this.tempSelectedColumns.filter((c) => c !== col)
    },
    clearAll() {
      this.tempSelectedColumns = []
    },
    onSelectionChange() {
      // Optional: Enforcement logic if needed
    },

    // --- Matrix Export ---
    async exportToExcel() {
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet('Attendance')

      const sortedItems = sortDropdownOptions([...this.items], 'name')

      this.maxManagers = 0
      sortedItems.forEach((employee) => {
        const managerChain = this.reporteeChain[employee.employeeId]?.managerChain || ''
        const managerList = managerChain ? managerChain.split(' -> ') : []
        this.maxManagers = Math.max(this.maxManagers, managerList.length)
      })

      const managerHeaders = Array.from(
        { length: this.maxManagers },
        (_, i) => `Manager Level ${i + 1}`
      )

      // --- Header Row 1 (Main) ---
      const mainHeaderRow = worksheet.getRow(1)
      // --- Header Row 2 (Sub) ---
      const subHeaderRow = worksheet.getRow(2)

      let colIndex = 1

      let headerColorIndex = 0

      // 1. Static Columns
      const staticHeaders = [
        'Name',
        `${this.orgLanguage?.employees || 'Employee'} Id`,
        'Role',
        'Designation',
        `${this.orgLanguage?.employees || 'Employee'} City`,
        ...managerHeaders,
        'Manager Hierarchy'
      ]

      staticHeaders.forEach((header) => {
        const cell = mainHeaderRow.getCell(colIndex)
        cell.value = header
        cell.font = { bold: true, size: 12 }
        cell.alignment = { horizontal: 'center', vertical: 'middle' }
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: headerColorIndex % 2 === 0 ? 'FFCFE2F3' : 'FFD9EAD3' }
        }
        headerColorIndex++
        worksheet.mergeCells(1, colIndex, 2, colIndex)
        colIndex++
      })

      // 2. Date Columns
      // UPDATED: Using tempSelectedColumns to respect dialog selection
      this.currentMonthDates.forEach((date) => {
        // Main Header: Date
        const startCol = colIndex
        let colSpan = 0

        const dateCols = []
        // Filter attendance cols from user selection logic
        // We use attendanceSubColumns order to keep things consistent
        this.attendanceSubColumns.forEach((colObj) => {
          if (this.tempSelectedColumns.includes(colObj.label)) {
            dateCols.push(colObj.label)
          }
        })

        colSpan = dateCols.length

        // Merge Main Header
        if (colSpan > 0) {
          worksheet.mergeCells(1, startCol, 1, startCol + colSpan - 1)
          const cell = mainHeaderRow.getCell(startCol)
          cell.value = date
          cell.font = { bold: true, size: 12 }
          cell.alignment = { horizontal: 'center', vertical: 'middle' }
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: headerColorIndex % 2 === 0 ? 'FFCFE2F3' : 'FFD9EAD3' }
          }
          headerColorIndex++
        }

        // Sub Headers
        dateCols.forEach((col) => {
          const subCell = subHeaderRow.getCell(colIndex)
          subCell.value = col
          subCell.font = { bold: true }
          subCell.alignment = { horizontal: 'center', vertical: 'middle' }
          colIndex++
        })
      })

      // 3. Summary Columns
      const summaryHeaders = [
        'Total Absent',
        'Total Present',
        'Total Week-off',
        'Total Miss punch',
        'Total Leaves',
        'Clock In (in %)'
      ]

      summaryHeaders.forEach((header) => {
        const cell = mainHeaderRow.getCell(colIndex)
        cell.value = header
        cell.font = { bold: true, size: 12 }
        cell.alignment = { horizontal: 'center', vertical: 'middle' }
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: headerColorIndex % 2 === 0 ? 'FFCFE2F3' : 'FFD9EAD3' }
        }
        headerColorIndex++
        worksheet.mergeCells(1, colIndex, 2, colIndex)
        colIndex++
      })

      mainHeaderRow.height = 30
      subHeaderRow.height = 25

      // --- Data Rows ---
      sortedItems.forEach((item) => {
        const chain = this.reporteeChain[item.employeeId]?.managerChain || ''
        const list = chain ? chain.split(' -> ') : []
        const managerCols = Array.from({ length: this.maxManagers }, (_, i) => list[i] || '-')

        const row = [
          item.name,
          item.orgEmployeeId,
          item.role,
          item.designation,
          item.city,
          ...managerCols,
          chain
        ]

        let totalAbsent = 0
        let totalPresent = 0
        let totalWeekOff = 0
        let totalMissPunch = 0
        let totalLeaves = 0
        let daysCount = 0

        this.currentMonthDates.forEach((date) => {
          daysCount++
          // Regular Cols
          this.attendanceSubColumns.forEach((colObj) => {
            const column = colObj.label
            if (this.tempSelectedColumns.includes(column)) {
              if (column === 'Clock-In Image') {
                row.push(item.attendance[date]?.clockInImage || '-')
              } else if (column === 'Clock-Out Image') {
                row.push(item.attendance[date]?.clockOutImage || '-')
              } else if (column === 'Planned Working Hour') {
                const val = this.attendanceSettings?.workingHours
                row.push(val ? `${val} Hours` : '-')
              } else if (column === 'Completed Working Hour') {
                const val = item.attendance[date]?.['Working Hours'] || ''
                row.push(val)
              } else {
                const value = item.attendance[date]?.[column] || ''
                row.push(value)

                if (value === 'ABSENT') totalAbsent++
                if (value === 'PRESENT') totalPresent++
                if (value === 'WEEK_OFF') totalWeekOff++
                if (value === 'LEAVE') totalLeaves++
                if (value === 'MISS_PUNCH') totalMissPunch++
              }
            }
          })
        })

        const clockInPercentage =
          daysCount > 0 ? `${((totalPresent * 100) / daysCount).toFixed(2)} %` : '0.00 %'

        row.push(
          totalAbsent,
          totalPresent,
          totalWeekOff,
          totalMissPunch,
          totalLeaves,
          clockInPercentage
        )
        worksheet.addRow(row)
      })

      const fileName = `Attendance_${this.startDate || ''}_${this.endDate || ''}.xlsx`
      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    // --- Formatted Data Export ---
    formatData() {
      const formattedData = []
      const sortedItems = sortDropdownOptions(this.items, 'name')

      // Filter columns based on user selection in the Dialog (Source of Truth)
      // Use attendanceSubColumns order
      const attendanceColsToExport = this.attendanceSubColumns
        .filter((colObj) => this.tempSelectedColumns.includes(colObj.label))
        .map((colObj) => colObj.label)
      const extraColsToExport = this.extraOptions.filter((col) =>
        this.tempSelectedColumns.includes(col)
      )

      let serialNumber = 1
      this.maxManagers = 0
      sortedItems.forEach((employee) => {
        const managerChain = this.reporteeChain[employee.employeeId]?.managerChain || ''
        const managerList = managerChain ? managerChain.split(' -> ') : []
        this.maxManagers = Math.max(this.maxManagers, managerList.length)
      })

      sortedItems.forEach((employee) => {
        let totalDistance = 0
        let showTotalDistance = attendanceColsToExport.includes('Distance (km)')

        this.currentMonthDates.forEach((date) => {
          const attendance = employee.attendance[date] || {}
          if (showTotalDistance && Number(attendance['Distance (km)'])) {
            totalDistance += Number(attendance['Distance (km)'])
          }
        })

        this.currentMonthDates.forEach((date, index) => {
          const newDate = date + this.$dayjs(this.startDate).format('YYYY')
          const formattedDate = this.$dayjs(newDate).format('DD/MM/YYYY')
          let isFirstPairForDate = true

          const attendance = employee.attendance[date] || {}
          const reporteeChain = this.reporteeChain[employee.employeeId]?.managerChain || ''
          const managerList = reporteeChain ? reporteeChain.split(' -> ') : []

          const managerColumns = Array.from(
            { length: this.maxManagers },
            (_, i) => managerList[i] || '-'
          )

          const baseRow = [
            index === 0 ? serialNumber : '',
            index === 0 ? employee.name : '',
            index === 0 ? employee.orgEmployeeId : '',
            index === 0 ? employee.role : '',
            index === 0 ? employee.designation : '',
            index === 0 ? employee.city : '',
            index === 0 ? employee.mobile : '',
            ...managerColumns,
            index === 0 ? reporteeChain : '',
            formattedDate,
            ...attendanceColsToExport.map((col) => {
              if (col === 'Planned Working Hour') {
                const val = this.attendanceSettings?.workingHours
                return val ? `${val} Hours` : '-'
              }
              if (col === 'Completed Working Hour') {
                return attendance['Working Hours'] || '-'
              }
              return attendance[col] || '-'
            })
          ]

          if (Array.isArray(attendance.pairDistance) && extraColsToExport.length > 0) {
            attendance.pairDistance.forEach((pair, i) => {
              const customerName = pair.customerName || '-'

              const pairRow = [
                isFirstPairForDate ? baseRow[0] : '',
                isFirstPairForDate ? baseRow[1] : '',
                isFirstPairForDate ? baseRow[2] : '',
                isFirstPairForDate ? baseRow[3] : '',
                isFirstPairForDate ? baseRow[4] : '',
                isFirstPairForDate ? baseRow[5] : '',
                isFirstPairForDate ? baseRow[6] : '',
                ...managerColumns.map((m) => (isFirstPairForDate && index === 0 ? m : '')),
                isFirstPairForDate && index === 0 ? reporteeChain : '',
                isFirstPairForDate ? formattedDate : '',
                ...attendanceColsToExport.map((col, j) => {
                  if (col === 'Planned Working Hour') {
                    const val = this.attendanceSettings?.workingHours
                    return isFirstPairForDate ? (val ? `${val} Hours` : '-') : ''
                  }
                  if (col === 'Completed Working Hour') {
                    return isFirstPairForDate ? attendance['Working Hours'] || '-' : ''
                  }
                  return isFirstPairForDate ? attendance[col] || '-' : ''
                }),
                customerName,
                ...extraColsToExport.map((col) => {
                  switch (col) {
                    case 'Origin Action':
                      return pair.originAction || '-'
                    case 'Destination Action':
                      return pair.destinationAction || '-'
                    case 'Origin':
                      return pair.origin || '-'
                    case 'Destination':
                      return pair.destination || '-'
                    case 'Distance B/W Origin Destination(in Km)':
                      return pair[`pair_${i + 1}`] !== undefined ? `${pair[`pair_${i + 1}`]}` : '-'
                    default:
                      return '-'
                  }
                }),
                index === 0 && i === 0 && showTotalDistance ? `${totalDistance.toFixed(2)}` : ''
              ]
              formattedData.push(pairRow)
              isFirstPairForDate = false
            })
          } else {
            const rowWithoutPairs = [
              ...baseRow,
              '-', // Customer
              ...extraColsToExport.map(() => '-'),
              index === 0 && showTotalDistance ? `${totalDistance.toFixed(2)}` : ''
            ]
            formattedData.push(rowWithoutPairs)
          }
          return
        })

        serialNumber++
      })

      return formattedData
    },

    openDialog() {
      this.openSelection = true
      // Auto-select all visible columns by default if none selected
      if (!this.tempSelectedColumns.length) {
        this.tempSelectedColumns = this.attendanceSubColumns
          .filter((c) => c.visible)
          .map((c) => c.label)
      }
      if (this.isExportDialog) return
    },
    openExportDialog() {
      this.openSelection = true
      this.isExportDialog = true
      // Auto-select all visible columns by default if none selected
      if (!this.tempSelectedColumns.length) {
        this.tempSelectedColumns = this.attendanceSubColumns
          .filter((c) => c.visible)
          .map((c) => c.label)
      }
    },
    printData() {
      if (this.isExportDialog) {
        this.exportToExcel()
      } else {
        this.formattedData = this.formatData()
        this.exportFormattedExcel()
      }
      this.cancel()
    },
    cancel() {
      this.$emit('cancel')
      this.openSelection = false
      this.isExportDialog = false
    },
    async exportFormattedExcel() {
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet('Attendance Data')

      const managerHeaders = Array.from(
        { length: this.maxManagers },
        (_, i) => `Manager Level ${i + 1}`
      )

      // Use attendanceSubColumns order
      const attendanceColsToExport = this.attendanceSubColumns
        .filter((colObj) => this.tempSelectedColumns.includes(colObj.label))
        .map((colObj) => colObj.label)
      const extraColsToExport = this.extraOptions.filter((col) =>
        this.tempSelectedColumns.includes(col)
      )

      const headers = [
        'Sr.No',
        `${this.orgLanguage?.employees || 'Employee'} Name`,
        `${this.orgLanguage?.employees || 'Employee'} Id`,
        'Role',
        'Designation',
        `${this.orgLanguage?.employees || 'Employee'} City`,
        `${this.orgLanguage?.employees || 'Employee'} Mobile Number`,
        ...managerHeaders,
        'Manager Hierarchy',
        'Date',
        ...attendanceColsToExport,
        `${this.tabMapping?.customerTable?.displayName || 'Customer'} Name`,
        ...extraColsToExport,
        'Monthly Distance (km)'
      ]

      // Header Row (Row 1)
      const headerRow = worksheet.getRow(1)
      headerRow.values = headers
      headerRow.font = { bold: true, size: 12 }
      headerRow.alignment = { horizontal: 'center', vertical: 'middle' }
      headerRow.eachCell((cell, colNumber) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: (colNumber - 1) % 2 === 0 ? 'FFCFE2F3' : 'FFD9EAD3' }
        }
      })

      // Data Rows
      this.formattedData.forEach((row) => {
        worksheet.addRow(row)
      })

      // Download
      const fileName = `AttendanceData_${this.startDate || ''}_${this.endDate || ''}.xlsx`
      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  },
  mounted() {
    this.$root.$on('export-excel', this.openExportDialog)
    this.$root.$on('export-format-excel', this.openDialog)
  },
  beforeDestroy() {
    this.$root.$off('export-excel', this.openExportDialog)
    this.$root.$off('export-format-excel', this.openDialog)
  }
}
</script>

<style scoped>
.custom-checkbox {
  margin-top: 0 !important;
}
</style>
